import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { getAuthHeaders } from '../../auth';
import Select2Dropdown from '../common/Select2Dropdown';
import ConfirmDialog from '../common/ConfirmDialog';
import TableScrollContainer from '../common/TableScrollContainer';
import { openFilePreview } from '../common/FilePreviewModal';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import the permissions hook
import useSortableTable from '../common/useSortableTable';
import JobForm from '../documentation/JobForm';
import ConditionalPermits from './ConditionalPermits';

const API_URL = '/api/documentation/jobs/permit-report/';
const JOBS_API_URL = '/api/documentation/jobs/';
const ADMIN_API_URL = '/api/admin/'; // Use admin panel URL for mappings

const PROCESSING_STATUS_OPTIONS = [
    { value: 'WAITING_FOR_CONTROL', label: 'Waiting for Control' },
    { value: 'WAITING_PAYMENT', label: 'Waiting Payment' },
    { value: 'PAID', label: 'Paid' },
];

const PermitReport = () => {
    const { hasPermission } = usePermissions(); // 2. Get the permission checker function
    // hasPermission already returns true for superusers; this covers them too.
    const canRevertPermit = hasPermission('PERMIT REPORTS', 'can_delete');
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('pending');
    const [releaseStatus, setReleaseStatus] = useState('');

    // State for the Job Form modal
    const [jobFormModal, setJobFormModal] = useState({ isOpen: false, job: null });

    // State for the dialog
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPermit, setCurrentPermit] = useState(null); // { jobId, permitType, blNumber }
    const [authNumber, setAuthNumber] = useState('');
    const [remarks, setRemarks] = useState('');
    const [processingStatus, setProcessingStatus] = useState('');
    const [saving, setSaving] = useState(false);
    // Conditional-release fields shown while approving a conditional-type permit
    const [isApproving, setIsApproving] = useState(false);
    const [sampleCollected, setSampleCollected] = useState(null); // null | true | false
    const [sampleCollectedDate, setSampleCollectedDate] = useState('');
    const [sampleLocation, setSampleLocation] = useState('');
    
    // State for Adding Manual Permit
    const [isAddPermitOpen, setIsAddPermitOpen] = useState(false);
    const [jobToAddPermit, setJobToAddPermit] = useState(null);
    const [selectedPermitType, setSelectedPermitType] = useState(null);

    // Reworked state to handle mapped documents
    const [documentsToUpload, setDocumentsToUpload] = useState([]);
    const [permitMappings, setPermitMappings] = useState([]);
    const [replacingDocType, setReplacingDocType] = useState(null);

    // State for Search and Filter
    const [searchQuery, setSearchQuery] = useState('');
    const [customsCompletedFilter, setCustomsCompletedFilter] = useState(false);
    const [permitTypeFilter, setPermitTypeFilter] = useState('');
    const [processingStatusFilter, setProcessingStatusFilter] = useState('');
    const [manifestOutFilter, setManifestOutFilter] = useState(false);

    // State for Pagination (Completed Jobs only)
    const [completedCurrentPage, setCompletedCurrentPage] = useState(1);
    const [completedItemsPerPage, setCompletedItemsPerPage] = useState(25);

    // State for Completed-tab revert confirmation
    const [permitRevertConfirm, setPermitRevertConfirm] = useState({ open: false, jobId: null, lpcoId: null, permitType: '', idfNo: '' });

    // State for Rejection Modal
    const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [permitToReject, setPermitToReject] = useState(null); // { jobId, lpcoId }

    const fetchReportData = useCallback(async (silent = false, search = '') => {
        if (!silent) setLoading(true);
        try {
            const res = await fetch(search ? `${API_URL}?search=${encodeURIComponent(search)}` : API_URL, { headers: getAuthHeaders() });
            if (!res.ok) {
                throw new Error('Failed to fetch permit report data');
            }
            const data = await res.json();
            setReportData(data);
        } catch (error) {
            toast.error(error.message);
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchReportData();
        fetchPermitMappings(); // Fetch mappings on component mount
    }, [fetchReportData]);

    // Debounced server search — lets the query reach permits approved beyond the
    // default 2-month window. Clearing the box restores the limited dataset.
    const isInitialSearch = useRef(true);
    useEffect(() => {
        if (isInitialSearch.current) { isInitialSearch.current = false; return; }
        const t = setTimeout(() => fetchReportData(true, searchQuery.trim()), 400);
        return () => clearTimeout(t);
    }, [searchQuery, fetchReportData]);


    const fetchPermitMappings = async () => {
        try {
            const res = await fetch(`${ADMIN_API_URL}permit-document-mappings/`, { headers: getAuthHeaders() });
            if (res.ok) {
                const data = await res.json();
                setPermitMappings(data);
            }
        } catch (error) {
            toast.error("Failed to load permit-document mappings.");
        }
    };

    const openJobFormModal = async (jobId) => {
        try {
            const res = await fetch(`${JOBS_API_URL}${jobId}/`, { headers: getAuthHeaders() });
            if (!res.ok) { toast.error('Failed to load job details'); return; }
            const data = await res.json();
            setJobFormModal({ isOpen: true, job: data });
        } catch (e) {
            toast.error('Error loading job');
        }
    };

    const closeJobFormModal = () => {
        setJobFormModal({ isOpen: false, job: null });
    };

    const handleOpenModal = async (jobId, permitType, blNumber, existingAuthNumber = '', existingRemarks = '', lpcoId = null, existingProcessingStatus = '', existingStatus = '', existingReleaseStatus = '', permitData = null, approving = false) => {
        const mapping = permitMappings.find(m => m.permit_type === permitType);
        let requiredDocTypes = mapping ? mapping.document_types.map(dt => dt.name) : [];

        if (permitType === 'GCLA' && !requiredDocTypes.some(dt => dt.toUpperCase() === 'GCLA TRANSPORT PERMIT')) {
            requiredDocTypes.push('GCLA TRANSPORT PERMIT');
        }

        // PVOC requires the Inspection Batch Certificate for Conditional/Monitoring release
        if ((permitType || '').toUpperCase().includes('PVOC') && !requiredDocTypes.some(dt => dt.toUpperCase() === 'INSPECTION BATCH CERTIFICATE')) {
            requiredDocTypes.push('INSPECTION BATCH CERTIFICATE');
        }

        // Open modal immediately with empty doc slots, then populate once docs are fetched
        setDocumentsToUpload(requiredDocTypes.map(docType => ({
            document_type: docType,
            existing_document: null,
            file_to_upload: null,
            previewUrl: null,
        })));
        setCurrentPermit({ jobId, permitType, blNumber, lpcoId, existingStatus });
        setAuthNumber(existingAuthNumber);
        setRemarks(existingRemarks);
        setProcessingStatus(existingProcessingStatus || '');
        setReleaseStatus(existingReleaseStatus || '');
        // Saved sample state is displayed as-is (Yes or No) when viewing a record;
        // when approving, it always starts unselected — the user must pick one.
        setSampleCollected(!approving && permitData && typeof permitData.sample_collected === 'boolean' ? permitData.sample_collected : null);
        setSampleCollectedDate(permitData?.sample_collected_date || '');
        setSampleLocation(permitData?.sample_location || '');
        setIsApproving(approving);
        setReplacingDocType(null);
        setIsModalOpen(true);

        // Lazily fetch job documents
        try {
            const res = await fetch(`${JOBS_API_URL}${jobId}/documents/`, { headers: getAuthHeaders() });
            if (res.ok) {
                const jobDocs = await res.json();
                setDocumentsToUpload(requiredDocTypes.map(docType => {
                    let existingDoc = jobDocs.find(d => d.document_type === docType);
                    if (!existingDoc && docType.toUpperCase() === 'GCLA TRANSPORT PERMIT') {
                        existingDoc = jobDocs.find(d => d.document_type === 'TRANSPORT PERMIT');
                    }
                    return {
                        document_type: docType,
                        existing_document: existingDoc || null,
                        file_to_upload: null,
                        previewUrl: null,
                    };
                }));
            }
        } catch (e) {
            // Non-fatal — modal still works, just won't show existing docs
            toast.error('Could not load existing documents.');
        }
    };

    const handleCloseModal = () => {
        // 2. Clean up any created object URLs to prevent memory leaks
        documentsToUpload.forEach(doc => {
            if (doc.previewUrl) {
                URL.revokeObjectURL(doc.previewUrl);
            }
        });
        setIsModalOpen(false);
        setCurrentPermit(null);
        setReleaseStatus('');
        setSampleCollected(null);
        setSampleCollectedDate('');
        setSampleLocation('');
        setIsApproving(false);
        setDocumentsToUpload([]); // Clear documents on close
    };

    const handleFileChange = (docType, file) => {
        // 3. Create a temporary URL for the selected file for previewing
        const previewUrl = file ? URL.createObjectURL(file) : null;
        setDocumentsToUpload(prev => prev.map(doc => {
            // Revoke old URL if it exists to prevent memory leaks
            if (doc.document_type === docType && doc.previewUrl) {
                URL.revokeObjectURL(doc.previewUrl);
            }
            return doc.document_type === docType ? { ...doc, file_to_upload: file, previewUrl: previewUrl } : doc;
        }));
        setReplacingDocType(null); // Exit replacing mode after selecting a file
    };

    const cancelFileSelection = (docType) => {
        setDocumentsToUpload(prev => prev.map(doc => {
            if (doc.document_type === docType) {
                // 4. Revoke URL before clearing the file selection
                if (doc.previewUrl) {
                    URL.revokeObjectURL(doc.previewUrl);
                }
                return { ...doc, file_to_upload: null, previewUrl: null };
            }
            return doc;
        }));
    };

    const handleSavePermit = async () => {
        // Add a permission check before saving
        if (!hasPermission('PERMIT REPORTS', 'can_edit')) {
            toast.error("You do not have permission to perform this action.");
            return;
        }

        const validDocuments = documentsToUpload.filter(d => d.file_to_upload);

        // Conditional options only apply while approving a conditional-type permit
        // (or when re-opening an already-approved one) — never for submitted permits.
        const isConditionalType = isConditionalPermitType(currentPermit.permitType);
        const isPvoc = (currentPermit.permitType || '').toUpperCase().includes('PVOC');
        const showConditionalSection = isConditionalType && (currentPermit.existingStatus === 'APPROVED' || isApproving);

        let effectiveReleaseStatus = releaseStatus;
        if (showConditionalSection) {
            if (sampleCollected === null) {
                toast.error('Please select whether a sample has been collected (Yes or No).');
                return;
            }
            if (sampleCollected === true) {
                if (isPvoc && !releaseStatus) {
                    toast.error('Please select a Release Status (Conditional or Monitoring).');
                    return;
                }
                if (!sampleLocation) {
                    toast.error('Please select where the sample was collected (Port or Client Premises).');
                    return;
                }
                // Date Collected is only required when the sample was taken at the Port;
                // Client Premises samples get their date from the Sample @ Client flow.
                if (sampleLocation === 'PORT' && !sampleCollectedDate) {
                    toast.error('Please enter the date the sample was collected.');
                    return;
                }
                // PVOC requires the Inspection Batch Certificate once Conditional or Monitoring is selected
                if (isPvoc) {
                    const batchDoc = documentsToUpload.find(d => (d.document_type || '').toUpperCase() === 'INSPECTION BATCH CERTIFICATE');
                    if (!batchDoc || (!batchDoc.file_to_upload && !batchDoc.existing_document)) {
                        toast.error('Please attach the Inspection Batch Certificate for the selected release status.');
                        return;
                    }
                }
            }
            // Collected sample => Conditional/Monitoring (PVOC picks one); no sample => normal release
            effectiveReleaseStatus = sampleCollected === true
                ? (isPvoc ? releaseStatus : 'CONDITIONAL')
                : 'NORMAL';
            if (!remarks.trim()) {
                toast.error('Remarks are required when a Release Status is selected.');
                return;
            }
        }

        if (!isApproving && validDocuments.length === 0 && !authNumber && !currentPermit.lpcoId && !processingStatus && !remarks) {
            toast.info('Nothing to save. Please upload a new document, enter an application number, select a processing status, or add remarks.');
            return;
        }

        // Remarks-only saves keep the permit pending; application no / processing status marks it submitted.
        const newLpcoStatus = (authNumber || processingStatus) ? 'SUBMITTED' : 'PENDING';

        setSaving(true);

        try {
            // --- Step 1: Handle Document Upload (if any new files are selected) ---
            if (validDocuments.length > 0) {
                const docPayload = new FormData();
                validDocuments.forEach((doc, index) => {
                    docPayload.append(`file_${index}`, doc.file_to_upload);
                    docPayload.append(`doc_${index}`, JSON.stringify({
                        document_type: doc.document_type,
                        remarks: `Permit Application: ${authNumber || 'Document Only'}`
                    }));
                });

                const docRes = await fetch(`${JOBS_API_URL}${currentPermit.jobId}/save-documents/`, {
                    method: 'POST',
                    headers: getAuthHeaders(true),
                    body: docPayload,
                });

                if (!docRes.ok) {
                    throw new Error('Failed to upload permit document(s).');
                }
                toast.success(`${validDocuments.length} document(s) uploaded successfully!`);
            }

            // --- Step 2: Handle LPCO/Permit Data Save ---
            if (currentPermit.lpcoId) {
                const patchPayload = { application_no: authNumber, processing_status: processingStatus, remarks };
                if (showConditionalSection) {
                    patchPayload.release_status = effectiveReleaseStatus;
                    patchPayload.sample_collected = sampleCollected === true;
                    // Empty string is not a valid DateField value — send null when unset
                    patchPayload.sample_collected_date = (sampleCollected === true && sampleCollectedDate) ? sampleCollectedDate : null;
                    patchPayload.sample_location = sampleCollected === true ? sampleLocation : '';
                }
                // Only touch the status for PENDING (remarks-only) records — upgrading on submission,
                // never downgrading an already SUBMITTED/APPROVED permit.
                if (currentPermit.existingStatus === 'PENDING') {
                    patchPayload.status = newLpcoStatus;
                }
                // Approve only once the conditional options have been selected and saved here
                if (isApproving) {
                    patchPayload.status = 'APPROVED';
                    patchPayload.approval_date = new Date().toISOString().split('T')[0];
                }
                const patchRes = await fetch(`${JOBS_API_URL}${currentPermit.jobId}/lpcos/${currentPermit.lpcoId}/`, {
                    method: 'PATCH',
                    headers: getAuthHeaders(),
                    body: JSON.stringify(patchPayload)
                });
                if (!patchRes.ok) {
                    const errorData = await patchRes.json().catch(() => ({}));
                    throw new Error(errorData.detail || 'Failed to update permit details.');
                }
                toast.success('Permit details updated successfully!');
            } else if (authNumber || processingStatus || remarks) {
                toast.info('Saving permit details...');
                const lpcoRes = await fetch(`${JOBS_API_URL}${currentPermit.jobId}/lpcos/`, { headers: getAuthHeaders() });
                if (!lpcoRes.ok) throw new Error('Failed to fetch existing permit data for update.');
                const existingLpcos = await lpcoRes.json();

                const newLpco = {
                    authority: currentPermit.permitType,
                    application_no: authNumber,
                    remarks: remarks,
                    processing_status: processingStatus,
                    status: newLpcoStatus,
                };
                const updatedLpcos = [...existingLpcos, newLpco];

                const saveLpcoRes = await fetch(`${JOBS_API_URL}${currentPermit.jobId}/lpcos/`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify(updatedLpcos),
                });

                if (!saveLpcoRes.ok) {
                    const errorData = await saveLpcoRes.json();
                    throw new Error(JSON.stringify(errorData) || 'Failed to save permit number.');
                }
                toast.success('Permit details saved successfully!');
            }

            // --- Final Step: Cleanup and Refresh ---
            handleCloseModal();
            fetchReportData(true, searchQuery); // Silent refresh — quick update, no reload

        } catch (error) {
            toast.error(`Save failed: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleStatusUpdate = async (jobId, lpcoId, newStatus, reason = null) => {
        const canUpdate = hasPermission('PERMIT REPORTS', 'can_edit')
            || (newStatus === 'SUBMITTED' && canRevertPermit);
        if (!canUpdate) {
             toast.error("Permission denied.");
             return;
        }

        try {
           const payload = { status: newStatus };
           if (newStatus === 'APPROVED') {
                payload.approval_date = new Date().toISOString().split('T')[0];
           }
           if (newStatus === 'SUBMITTED') {
                payload.approval_date = null;
                // Reverting an approval also clears all conditional workflow values
                payload.release_status = '';
                payload.sample_collected = false;
                payload.sample_collected_date = null;
                payload.sample_location = '';
                payload.conditional_result = '';
                payload.guidance_letter_released = '';
           }
           if (newStatus === 'REJECTED') {
               payload.rejection_reason = reason;
           }

           const res = await fetch(`${JOBS_API_URL}${jobId}/lpcos/${lpcoId}/`, {
               method: 'PATCH',
               headers: getAuthHeaders(),
               body: JSON.stringify(payload)
           });
           
           if (!res.ok) {
               const errorData = await res.json().catch(() => ({}));
               throw new Error(errorData.detail || 'Failed to update status');
           }
           
           if (newStatus === 'REJECTED') {
               toast.info('Permit rejected.');
           } else {
               toast.success(`Permit status updated to ${newStatus}`);
           }
           
           setIsRejectionModalOpen(false);
           setRejectionReason('');
           setPermitToReject(null);
           fetchReportData(true, searchQuery); // Silent refresh — quick update, no reload
        } catch (error) {
            toast.error(error.message);
        }
    };

    const confirmRejection = () => {
        if (!permitToReject) return;
        handleStatusUpdate(permitToReject.jobId, permitToReject.lpcoId, 'REJECTED', rejectionReason);
    };

    const filteredReportData = useMemo(() => {
        let data = reportData;
        
        if (searchQuery) {
            const lowerQuery = searchQuery.toLowerCase();
            data = data.filter(job => 
                (job.id && job.id.toString().includes(lowerQuery)) ||
                (job.client_name && job.client_name.toLowerCase().includes(lowerQuery)) ||
                (job.bl_number && job.bl_number.toLowerCase().includes(lowerQuery)) ||
                (job.idf_no && job.idf_no.toLowerCase().includes(lowerQuery)) ||
                (job.tansad_no && job.tansad_no.toLowerCase().includes(lowerQuery)) ||
                (job.ucr_no && job.ucr_no.toLowerCase().includes(lowerQuery))
            );
        }
        return data; 
    }, [reportData, searchQuery]);

    const permitTypeFilterOptions = useMemo(() => {
        const types = new Set();
        reportData.forEach(job => {
            (job.all_required_permits || []).forEach(t => types.add(t));
            (job.submitted_permits || []).forEach(p => {
                if (p.authority) types.add(p.authority);
            });
        });
        return Array.from(types).sort().map(t => ({ id: t, text: t }));
    }, [reportData]);

    const processingStatusFilterOptions = useMemo(() =>
        PROCESSING_STATUS_OPTIONS.map(o => ({ id: o.value, text: o.label })),
        []
    );

    const pendingJobs = useMemo(() =>
        filteredReportData.filter(job => {
            const hasPendingPermits = job.pending_permits && job.pending_permits.length > 0;
            const hasSubmittedPermits = job.submitted_permits && job.submitted_permits.length > 0;
            if (!hasPendingPermits || hasSubmittedPermits) return false;
            if (permitTypeFilter && !((job.all_required_permits || []).includes(permitTypeFilter) || (job.pending_permits || []).includes(permitTypeFilter))) return false;
            if (processingStatusFilter && !((job.submitted_permits || []).some(p => p.processing_status === processingStatusFilter) || (job.completed_permits || []).some(p => p.processing_status === processingStatusFilter))) return false;
            if (manifestOutFilter && !job.tra_manifest_ready) return false;
            return true;
        }),
        [filteredReportData, permitTypeFilter, processingStatusFilter, manifestOutFilter]
    );

    const submittedJobs = useMemo(() =>
        filteredReportData.filter(job => {
            const hasSubmittedPermits = job.submitted_permits && job.submitted_permits.length > 0;
            if (!hasSubmittedPermits) return false;

            const noPendingPermits = (!job.pending_permits || job.pending_permits.length === 0);
            const allSubmittedApproved = (!job.submitted_permits || job.submitted_permits.every(p => p.status === 'APPROVED'));
            const hasCompletedPermits = (job.completed_permits && job.completed_permits.length > 0);
            const isCompleted = noPendingPermits && allSubmittedApproved && hasCompletedPermits;

            if (isCompleted) return false;
            if (permitTypeFilter && !(job.submitted_permits || []).some(p => p.authority === permitTypeFilter)) return false;
            if (processingStatusFilter && !(job.submitted_permits || []).some(p => p.processing_status === processingStatusFilter)) return false;
            if (manifestOutFilter && !job.tra_manifest_ready) return false;
            return true;
        }),
        [filteredReportData, permitTypeFilter, processingStatusFilter, manifestOutFilter]
    );

    // Awaiting Results count for the Conditional tab badge — mirrors the Awaiting
    // Results tab in ConditionalPermits: release status Conditional/Monitoring
    // with no test result recorded yet.
    const isConditionalPermitType = (permitType) => {
        const t = (permitType || '').toUpperCase();
        return t.includes('CONDITIONAL') || t.includes('TBS DI') || t.includes('PVOC');
    };

    // Release Status options for conditional-type permits (mirrors ConditionalPermits)
    const getReleaseStatusOptions = (permitType) => {
        if ((permitType || '').toUpperCase().includes('PVOC')) {
            return [
                { value: 'CONDITIONAL', label: 'Conditional' },
                { value: 'MONITORING', label: 'Monitoring' },
            ];
        }
        // TBS DI and other "Conditional" permit types
        return [
            { value: 'CONDITIONAL', label: 'Conditional' },
            { value: 'NORMAL', label: 'Normal' },
        ];
    };

    const conditionalAwaitingCount = useMemo(() => {
        const seen = new Set();
        let count = 0;
        filteredReportData.forEach(job => {
            [...(job.submitted_permits || []), ...(job.completed_permits || [])].forEach(permit => {
                if (!permit || seen.has(permit.id)) return;
                seen.add(permit.id);
                if (!isConditionalPermitType(permit.authority)) return;
                const release = (permit.release_status || '').toUpperCase();
                if (!['CONDITIONAL', 'MONITORING'].includes(release) || permit.conditional_result) return;
                count += 1;
            });
        });
        return count;
    }, [filteredReportData]);

    const completedJobs = useMemo(() => 
        filteredReportData.filter(job => {
            const noMissingOrRejected = (!job.pending_permits || job.pending_permits.length === 0);
            // Check if all submitted permits are approved.
            // Note: In backend, submitted_permits includes approved ones.
            const allSubmittedApproved = (!job.submitted_permits || job.submitted_permits.every(p => p.status === 'APPROVED'));
            
            // Should arguably have at least one permit to be "Completed" in this report context?
            // If all_required_permits is empty, it's trivial.
            // Let's ensure we are counting jobs that actually had requirements.
            const hasPermits = (job.completed_permits && job.completed_permits.length > 0);

            // If job has no permits required at all, should it show?
            // If we strictly follow "Completed PERMITS report", maybe only jobs with permits.
            // Let's keep it simple: No pending, and everything submitted is approved.
            // And ensure at least one completed permit is present so we don't list random jobs without permit requirements?
            // The previous logic had `job.completed_permits.length > 0`. Let's keep that safeguard.
            
            const isFullyApproved = noMissingOrRejected && allSubmittedApproved && hasPermits;

            if (!isFullyApproved) return false;
            
            if (customsCompletedFilter) {
                return job.duty_paid && job.wharfage_paid;
            }
            return true;
        }),
        [filteredReportData, customsCompletedFilter]
    );

    // Sorting logic — pending: accepted_date desc; submitted: submitted_date desc; completed: completed_date desc
    const { items: sortedPendingJobs, requestSort: requestSortPending, getSortIndicator: getSortIndicatorPending } = useSortableTable(pendingJobs, { key: 'accepted_date', direction: 'descending' });
    const { items: sortedSubmittedJobs, requestSort: requestSortSubmitted, getSortIndicator: getSortIndicatorSubmitted } = useSortableTable(submittedJobs, { key: 'submitted_date', direction: 'descending' });
    const { items: sortedCompletedJobs, requestSort: requestSortCompleted, getSortIndicator: getSortIndicatorCompleted } = useSortableTable(completedJobs, { key: 'completed_date', direction: 'descending' });

    // Pagination logic for completed jobs
    const completedTotalPages = Math.max(1, Math.ceil(sortedCompletedJobs.length / completedItemsPerPage));
    const completedCurrentPageClamped = Math.min(completedCurrentPage, completedTotalPages);
    const completedStart = (completedCurrentPageClamped - 1) * completedItemsPerPage;
    const paginatedCompletedJobs = useMemo(() => {
        return sortedCompletedJobs.slice(completedStart, completedStart + completedItemsPerPage);
    }, [sortedCompletedJobs, completedStart, completedItemsPerPage]);

    const handleCompletedPageChange = (page) => {
        setCompletedCurrentPage(page);
    };

    const handleCompletedItemsPerPageChange = (size) => {
        setCompletedItemsPerPage(size);
        setCompletedCurrentPage(1); // Reset to first page when changing items per page
    };

    const handleOpenAddPermit = (job) => {
        setJobToAddPermit(job);
        setSelectedPermitType(null);
        setIsAddPermitOpen(true);
    };

    const handleSaveManualPermit = async () => {
        if (!jobToAddPermit || !selectedPermitType) {
            toast.warn("Please select a permit type.");
            return;
        }
        
        // Prevent Adding Duplicate
        if (jobToAddPermit.all_required_permits.includes(selectedPermitType)) {
             toast.info("This permit is already required for this job.");
             setIsAddPermitOpen(false);
             return;
        }

        setSaving(true);
        try {
            // Get current manual permits from job (if available in reportData) or start fresh
            // Since we added manual_permits to report response, we can use it.
            // If not available (old data?), fallback to empty.
            // Wait, job object here is from reportData, so it should have manual_permits if we refreshed properly.
            const currentManual = jobToAddPermit.manual_permits || [];
            const newManual = [...currentManual, selectedPermitType];

            const res = await fetch(`${JOBS_API_URL}${jobToAddPermit.id}/`, {
                method: 'PATCH',
                headers: getAuthHeaders(),
                body: JSON.stringify({ manual_permits: newManual })
            });

            if (!res.ok) throw new Error("Failed to add permit.");
            
            toast.success("Permit added successfully.");
            setIsAddPermitOpen(false);
            setJobToAddPermit(null);
            fetchReportData(true, searchQuery); // Silent refresh — quick update, no reload
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteManualPermit = async (job, permitToDelete) => {
        if (!window.confirm(`Are you sure you want to remove ${permitToDelete}?`)) return;

        setSaving(true);
        try {
            const currentManual = job.manual_permits || [];
            const newManual = currentManual.filter(p => p !== permitToDelete);

            const res = await fetch(`${JOBS_API_URL}${job.id}/`, {
                method: 'PATCH',
                headers: getAuthHeaders(),
                body: JSON.stringify({ manual_permits: newManual })
            });

            if (!res.ok) throw new Error("Failed to remove permit.");
            
            toast.success("Permit removed successfully.");
            fetchReportData(true, searchQuery); // Silent refresh — quick update, no reload
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    const renderList = (items, isHsCodeList = false, isPendingList = false, job = null) => {
        if (!items || items.length === 0) return '-';
        
        if (isHsCodeList) {
            const uniqueHsCodesMap = new Map();
            items.forEach(detail => {
                if (!uniqueHsCodesMap.has(detail.hs_code)) {
                    uniqueHsCodesMap.set(detail.hs_code, { hs_code: detail.hs_code, permits: new Set(detail.permits) });
                } else {
                    detail.permits.forEach(p => uniqueHsCodesMap.get(detail.hs_code).permits.add(p));
                }
            });
            const uniqueItems = Array.from(uniqueHsCodesMap.values());
            return (
                <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                    {uniqueItems.map((detail, index) => (
                        <li key={index} title={`Permits: ${Array.from(detail.permits).join(', ')}`}>
                            {detail.hs_code}
                        </li>
                    ))}
                </ul>
            );
        }

        return (
            <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                {items.map((item, index) => {
                    if (isPendingList) {
                        const showExportLabel = job?.cargo_type?.toUpperCase() === 'ZNZ';
                        const exportLabel = showExportLabel ? (
                            <span style={{ color: 'red', marginLeft: 4, fontSize: '0.85em' }}>(Export)</span>
                        ) : null;
                        const pendingDetail = job?.pending_permit_details?.[item];
                        const pendingTitle = pendingDetail?.remarks ? `Remarks: ${pendingDetail.remarks.toUpperCase()}` : undefined;
                        // 3. Conditionally render an interactive link or plain text
                        if (hasPermission('PERMIT REPORTS', 'can_edit')) {
                            return (
                                <li key={index} title={pendingTitle}>
                                    <a href="#" onClick={(e) => { e.preventDefault(); handleOpenModal(job.id, item, job.bl_number, '', pendingDetail?.remarks || '', pendingDetail?.id || null, '', pendingDetail?.id ? 'PENDING' : '', '', pendingDetail); }}>
                                        {item}
                                    </a>
                                    {exportLabel}
                                </li>
                            );
                        }
                        return <li key={index} title={pendingTitle}>{item}{exportLabel}</li>; // Render as plain text if no edit permission
                    }

                    // Logic for Required Permits List (Manual Permits Deletion)
                    if (!isPendingList && !isHsCodeList && job && job.manual_permits && job.manual_permits.includes(item)) {
                        return (
                           <li key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                               <span>{item} <span style={{fontSize: '0.8em', color: '#666', fontStyle: 'italic'}}>(Manual)</span></span>
                               {hasPermission('PERMIT REPORTS', 'can_edit') && (
                                   <button 
                                       className="btn-link"
                                       onClick={() => handleDeleteManualPermit(job, item)}
                                       title="Remove manual permit"
                                       style={{ padding: 0, marginLeft: 10, border: 'none', background: 'none', color: '#dc3545', cursor: 'pointer' }}
                                   >
                                       <i className="fa fa-trash"></i>
                                   </button>
                               )}
                           </li>
                        );
                   }

                    const isObject = typeof item === 'object' && item !== null;
                    const text = isObject ? item.authority : item;
                    const title = isObject ? `Application No: ${item.application_no || 'N/A'}\nRemarks: ${item.remarks || 'N/A'}` : '';
                    if (isObject && job) {
                        return (
                            <li key={index} title={title} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <a href="#" onClick={(e) => { e.preventDefault(); handleOpenModal(job.id, item.authority, job.bl_number, item.application_no || '', item.remarks || '', item.id, item.processing_status || '', item.status || '', item.release_status || '', item); }}>
                                    {text}
                                </a>
                                {canRevertPermit && (
                                    <select
                                        className="form-control"
                                        style={{ padding: 2, fontSize: '0.85em', height: 'auto', width: 'auto' }}
                                        value={item.status || 'APPROVED'}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                            if (e.target.value === 'SUBMITTED') {
                                                setPermitRevertConfirm({
                                                    open: true,
                                                    jobId: job.id,
                                                    lpcoId: item.id,
                                                    permitType: item.authority,
                                                    idfNo: job.idf_no || '-',
                                                });
                                            }
                                        }}
                                    >
                                        <option value="APPROVED">Approved</option>
                                        <option value="SUBMITTED">Submitted</option>
                                    </select>
                                )}
                            </li>
                        );
                    }
                    return <li key={index} title={title}>{text}</li>;
                })}
            </ul>
        );
    };

    const fmtDate = (value) => {
        if (!value) return '-';
        const d = new Date(value);
        if (isNaN(d.getTime())) return '-';
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    };

    const renderTable = (jobs, tabType) => {
        const isPendingTab = tabType === 'pending';
        const isSubmittedTab = tabType === 'submitted';
        const isActionableTab = isPendingTab || isSubmittedTab;

        const requestSort = isPendingTab
            ? requestSortPending
            : isSubmittedTab
                ? requestSortSubmitted
                : requestSortCompleted;
        const getSortIndicator = isPendingTab
            ? getSortIndicatorPending
            : isSubmittedTab
                ? getSortIndicatorSubmitted
                : getSortIndicatorCompleted;
        
        return (
        <TableScrollContainer>
            <table className="jobs-table" style={{ overflow: 'visible' }}>
                <thead>
                    <tr>
                        <th onClick={() => requestSort('id')}>JOB ID {getSortIndicator('id')}</th>
                        <th onClick={() => requestSort('idf_no')}>IDF NO. {getSortIndicator('idf_no')}</th>
                        <th onClick={() => requestSort('tansad_no')}>TANSAD NO. {getSortIndicator('tansad_no')}</th>
                        <th onClick={() => requestSort('ucr_no')}>UCR NO. {getSortIndicator('ucr_no')}</th>
                        <th onClick={() => requestSort('client_name')}>CUSTOMER NAME {getSortIndicator('client_name')}</th>
                        <th onClick={() => requestSort('bl_number')}>BL NUMBER {getSortIndicator('bl_number')}</th>
                        {isPendingTab && <th onClick={() => requestSort('accepted_date')}>ACCEPTED DATE {getSortIndicator('accepted_date')}</th>}
                        {isSubmittedTab && <th onClick={() => requestSort('submitted_date')}>SUBMITTED DATE {getSortIndicator('submitted_date')}</th>}
                        {!isActionableTab && <th onClick={() => requestSort('completed_date')} style={{ whiteSpace: 'nowrap' }}>COMPLETED DATE<br/><span style={{ fontWeight: 'normal', fontSize: '0.85em' }}>ACCEPTED DATE</span> {getSortIndicator('completed_date')}</th>}
                        <th>HS CODES</th>
                        {isActionableTab && <th>REQUIRED PERMITS</th>}
                        {isActionableTab && <th>SUBMITTED PERMITS</th>}
                        <th>{isActionableTab ? 'PENDING PERMITS' : 'COMPLETED PERMITS'}</th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr><td colSpan={isActionableTab ? "11" : "9"} style={{ textAlign: 'center' }}>Loading...</td></tr>
                    ) : jobs.length === 0 ? (
                        <tr><td colSpan={isActionableTab ? "11" : "9"} className="no-data">No records found.</td></tr>
                    ) : (
                        jobs.map((job, index) => {
                            const hasManifest = isActionableTab && !!job.tra_manifest_ready;
                            const manifestTooltip = hasManifest ? (() => {
                                const d = new Date(job.tra_manifest_ready);
                                const dd = String(d.getDate()).padStart(2, '0');
                                const mm = String(d.getMonth() + 1).padStart(2, '0');
                                const yyyy = d.getFullYear();
                                return `Manifest Date: ${dd}/${mm}/${yyyy}`;
                            })() : undefined;
                            return (
                            <tr key={job.id} className={hasManifest && isPendingTab ? 'flash-red' : ''} title={manifestTooltip} style={hasManifest && isPendingTab ? undefined : { backgroundColor: index % 2 === 0 ? '#E3F2FD' : '#ffffff' }}>
                                <td>
                                    <a href="#" onClick={(e) => { e.preventDefault(); openJobFormModal(job.id); }}>
                                        {job.id}
                                    </a>
                                </td>
                                <td>{job.idf_no || '-'}</td>
                                <td>{job.tansad_no || '-'}</td>
                                <td>{job.ucr_no || '-'}</td>
                                <td><strong>{job.client_short_name || job.client_name}</strong></td>
                                <td>{job.bl_number}</td>
                                {isPendingTab && <td>{fmtDate(job.accepted_date)}</td>}
                                {isSubmittedTab && <td>{job.submitted_date || '-'}</td>}
                                {!isActionableTab && (
                                    <td>
                                        {(() => {
                                            const daysDiff = (job.completed_date && job.accepted_date)
                                                ? Math.round((new Date(job.completed_date) - new Date(job.accepted_date)) / (1000 * 60 * 60 * 24))
                                                : null;
                                            return (
                                                <>
                                                    <div>{job.completed_date || '-'}{daysDiff !== null && <span style={{ color: '#888', marginLeft: 4 }}>({daysDiff}d)</span>}</div>
                                                    <div style={{ fontSize: '0.85em', color: '#666', marginTop: '2px' }}>{fmtDate(job.accepted_date)}</div>
                                                </>
                                            );
                                        })()}
                                    </td>
                                )}
                                <td>{renderList(job.hs_code_details, true)}</td>
                                {isActionableTab && (
                                    <>
                                        <td>
                                            {renderList(job.all_required_permits, false, false, job)}
                                            {hasPermission('PERMIT REPORTS', 'can_edit') && (
                                                <button 
                                                    className="btn-small btn-info-small" 
                                                    style={{ marginTop: '5px', display: 'block', width: '100%' }}
                                                    onClick={() => handleOpenAddPermit(job)}
                                                    title="Add another required permit manually"
                                                >
                                                    <i className="fa fa-plus"></i> Add Permit
                                                </button>
                                            )}
                                        </td>
                                        <td>
                                            <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                                                {(!job.submitted_permits || job.submitted_permits.length === 0) ? '-' : job.submitted_permits.map((permit, idx) => (
                                                    <li key={idx} style={{ marginBottom: 10, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                                                        <div style={{fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 8}}>
                                                            <a href="#" onClick={(e) => { e.preventDefault(); handleOpenModal(job.id, permit.authority, job.bl_number, permit.application_no || '', permit.remarks || '', permit.id, permit.processing_status || '', permit.status || '', permit.release_status || '', permit); }}>
                                                                {permit.authority}
                                                            </a>
                                                            {job?.cargo_type?.toUpperCase() === 'ZNZ' && (
                                                                <span style={{ color: 'red', marginLeft: 4, fontSize: '0.85em' }}>(Export)</span>
                                                            )}
                                                            <span style={{ fontSize: '0.85em', color: permit.processing_status === 'PAID' ? 'green' : 'red' }}>
                                                                [{PROCESSING_STATUS_OPTIONS.find(o => o.value === permit.processing_status)?.label || permit.processing_status || '-'}]
                                                            </span>
                                                        </div>
                                                        <div style={{fontSize: '0.85em'}}>NO: {permit.application_no}</div>
                                                        <div style={{fontSize: '0.85em', color: '#666'}}>Date: {permit.submission_date || '-'}</div>
                                                        
                                                        {hasPermission('PERMIT REPORTS', 'can_edit') && (
                                                            <div style={{marginTop: 5}}>
                                                                <select 
                                                                    className="form-control"
                                                                    style={{padding: 2, fontSize: '0.85em', height: 'auto'}}
                                                                    value={permit.status || 'SUBMITTED'}
                                                                    onChange={(e) => {
                                                                        if (e.target.value === 'APPROVED' && permit.processing_status !== 'PAID') {
                                                                            toast.error('Permit should be Paid before approving.');
                                                                            return;
                                                                        }
                                                                        // Conditional-type permits approve through the modal so the
                                                                        // conditional options are selected and saved first.
                                                                        if (e.target.value === 'APPROVED' && isConditionalPermitType(permit.authority)) {
                                                                            handleOpenModal(job.id, permit.authority, job.bl_number, permit.application_no || '', permit.remarks || '', permit.id, permit.processing_status || '', permit.status || '', permit.release_status || '', permit, true);
                                                                            return;
                                                                        }
                                                                        if (e.target.value === 'REJECTED') {
                                                                            setPermitToReject({ jobId: job.id, lpcoId: permit.id });
                                                                            setRejectionReason('');
                                                                            setIsRejectionModalOpen(true);
                                                                        } else {
                                                                            handleStatusUpdate(job.id, permit.id, e.target.value);
                                                                        }
                                                                    }}
                                                                >
                                                                    <option value="SUBMITTED">Submitted</option>
                                                                    <option value="APPROVED">Approved</option>
                                                                    <option value="REJECTED">Rejected</option>
                                                                </select>
                                                            </div>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        </td>
                                    </>
                                )}
                                <td>{renderList(isActionableTab ? job.pending_permits : job.completed_permits, false, isActionableTab, job)}</td>
                            </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </TableScrollContainer>
        );
    };

    // 4. Add a top-level permission check for viewing the report
    if (!hasPermission('PERMIT REPORTS', 'can_view')) {
        return (
            <div className="job-list-container" style={{ overflowX: 'auto' }}>
                <h2>Permission Denied</h2>
                <p>You do not have permission to view this report.</p>
            </div>
        );
    }

    return (
        <div className="job-list-container" style={{ overflowX: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2>Permit Reports</h2>
                <button onClick={fetchReportData} title="Refresh" style={{ background: 'none', border: '1px solid transparent', borderRadius: 6, cursor: 'pointer', fontSize: 18, color: '#444', padding: '4px 8px', transition: 'background 0.15s, border-color 0.15s' }} onMouseEnter={e => { e.currentTarget.style.background = '#f0f0f0'; e.currentTarget.style.borderColor = '#bbb'; }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'transparent'; }}><i className="fa-solid fa-rotate-right" style={{ fontWeight: 900 }} /></button>
            </div>

            <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', width: '50%' }}>
                <input
                    type="text"
                    placeholder="Search by ID, Client, BL, IDF, Tansad..."
                    className="form-control"
                    style={{ flex: 1, minWidth: 150 }}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />

                {(activeTab === 'pending' || activeTab === 'submitted') && (
                    <>
                        <div style={{ width: 150, minWidth: 0 }}>
                            <Select2Dropdown
                                options={permitTypeFilterOptions}
                                value={permitTypeFilter}
                                onChange={setPermitTypeFilter}
                                placeholder="Filter by Permit Type"
                                valueKey="id"
                                displayKey="text"
                                clearable
                            />
                        </div>
                        <div style={{ width: 150, minWidth: 0 }}>
                            <Select2Dropdown
                                options={processingStatusFilterOptions}
                                value={processingStatusFilter}
                                onChange={setProcessingStatusFilter}
                                placeholder="Filter by Processing Status"
                                valueKey="id"
                                displayKey="text"
                                clearable
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input
                                type="checkbox"
                                id="manifestOutFilter"
                                checked={manifestOutFilter}
                                onChange={(e) => setManifestOutFilter(e.target.checked)}
                            />
                            <label htmlFor="manifestOutFilter" style={{ cursor: 'pointer', userSelect: 'none' }}>MANIFEST OUT</label>
                        </div>
                    </>
                )}
                
                {activeTab === 'completed' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                         <input 
                            type="checkbox" 
                            id="customsFilter"
                            checked={customsCompletedFilter}
                            onChange={(e) => setCustomsCompletedFilter(e.target.checked)}
                         />
                         <label htmlFor="customsFilter" style={{ cursor: 'pointer', userSelect: 'none' }}>Customs Completed Only (Duty & Wharfage Paid)</label>
                    </div>
                )}
            </div>

            <div className="custom-tabs-container" style={{ marginTop: 16, marginBottom: 16 }}>
                <button
                    className={`custom-tab ${activeTab === 'pending' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pending')}
                >
                    Pending Permits ({pendingJobs.length})
                </button>
                <button
                    className={`custom-tab ${activeTab === 'submitted' ? 'active' : ''}`}
                    onClick={() => setActiveTab('submitted')}
                >
                    Submitted Permits ({submittedJobs.length})
                </button>
                <button
                    className={`custom-tab ${activeTab === 'completed' ? 'active' : ''}`}
                    onClick={() => setActiveTab('completed')}
                >
                    Completed Permits ({completedJobs.length})
                </button>
                <button
                    className={`custom-tab ${activeTab === 'conditional' ? 'active' : ''} ${conditionalAwaitingCount > 0 ? 'deposit-flashing' : ''}`}
                    onClick={() => setActiveTab('conditional')}
                >
                    Conditional ({conditionalAwaitingCount})
                </button>
            </div>

            <div className="tab-content">
                {activeTab === 'pending' ? (
                    <>
                        <div style={{ fontWeight: 'bold', background: '#eef5ff', padding: 8, borderRadius: 6, marginBottom: 8 }}>
                            PENDING PERMITS
                        </div>
                        {renderTable(sortedPendingJobs, 'pending')}
                    </>
                ) : activeTab === 'submitted' ? (
                    <>
                        <div style={{ fontWeight: 'bold', background: '#eef5ff', padding: 8, borderRadius: 6, marginBottom: 8 }}>
                            SUBMITTED PERMITS
                        </div>
                        {renderTable(sortedSubmittedJobs, 'submitted')}
                    </>
                ) : activeTab === 'completed' ? (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 'bold', background: '#eef5ff', padding: 8, borderRadius: 6, marginBottom: 8 }}>
                            <span>COMPLETED PERMITS</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                                <span style={{ color: '#555' }}>Rows per page:</span>
                                {[25, 50, 100].map(size => (
                                    <button key={size} onClick={() => handleCompletedItemsPerPageChange(size)}
                                        style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid #bbb', cursor: 'pointer', fontWeight: completedItemsPerPage === size ? 'bold' : 'normal', background: completedItemsPerPage === size ? '#007bff' : '#fff', color: completedItemsPerPage === size ? '#fff' : '#333' }}>
                                        {size}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {renderTable(paginatedCompletedJobs, 'completed')}
                        {completedTotalPages > 1 && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginTop: 8, fontSize: 13 }}>
                                <span style={{ color: '#555' }}>
                                    {completedStart + 1}–{Math.min(completedStart + completedItemsPerPage, sortedCompletedJobs.length)} of {sortedCompletedJobs.length}
                                </span>
                                <button onClick={() => handleCompletedPageChange(completedCurrentPage - 1)} disabled={completedCurrentPageClamped === 1}
                                    style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid #bbb', cursor: completedCurrentPageClamped === 1 ? 'default' : 'pointer', background: '#fff', color: completedCurrentPageClamped === 1 ? '#bbb' : '#333' }}>
                                    ‹ Prev
                                </button>
                                <span style={{ color: '#555' }}>Page {completedCurrentPageClamped} / {completedTotalPages}</span>
                                <button onClick={() => handleCompletedPageChange(completedCurrentPage + 1)} disabled={completedCurrentPageClamped === completedTotalPages}
                                    style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid #bbb', cursor: completedCurrentPageClamped === completedTotalPages ? 'default' : 'pointer', background: '#fff', color: completedCurrentPageClamped === completedTotalPages ? '#bbb' : '#333' }}>
                                    Next ›
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <ConditionalPermits searchQuery={searchQuery} />
                )}
            </div>

            <ConfirmDialog
                open={permitRevertConfirm.open}
                title="Change Permit Status"
                message={`Are you sure you would like to change the status of Permit Type ${permitRevertConfirm.permitType} for IDF ${permitRevertConfirm.idfNo}?`}
                onConfirm={() => {
                    setPermitRevertConfirm({ open: false, jobId: null, lpcoId: null, permitType: '', idfNo: '' });
                    handleStatusUpdate(permitRevertConfirm.jobId, permitRevertConfirm.lpcoId, 'SUBMITTED');
                }}
                onCancel={() => setPermitRevertConfirm({ open: false, jobId: null, lpcoId: null, permitType: '', idfNo: '' })}
            />

            {isRejectionModalOpen && (
                 <div className="modal-overlay">
                    <div className="dialog-container" style={{ width: 400 }}>
                        <div className="dialog-header">
                            <h3>Reject Permit</h3>
                        </div>
                        <div className="dialog-body">
                           <p>Please enter the reason for rejection.</p>
                           <textarea
                                className="form-control"
                                rows="3"
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                placeholder="Rejection reason..."
                                style={{ width: '100%', marginTop: 8 }}
                           />
                        </div>
                        <div className="dialog-footer" style={{marginTop: 20}}>
                             <button type="button" className="dialog-btn dialog-btn-secondary" onClick={() => setIsRejectionModalOpen(false)}>Cancel</button>
                             <button type="button" className="dialog-btn dialog-btn-danger" onClick={confirmRejection} disabled={!rejectionReason}>
                                 Reject Permit
                             </button>
                        </div>
                    </div>
                 </div>
            )}

             {isModalOpen && (
                <div className="modal-overlay">
                    <div className="dialog-container" style={{ width: 500 }}>
                        <div className="dialog-header">
                            <h3>Add Permit Details for BL: {currentPermit?.blNumber}</h3>
                        </div>
                        <div className="dialog-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                            <p style={{ marginBottom: 16 }}><strong>Permit Type:</strong> {currentPermit?.permitType}</p>
                            
                            {/* Mapped Documents Section */}
                            <div style={{ border: '1px solid #ddd', borderRadius: 4, padding: 12, marginBottom: 16 }}>
                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 8 }}>Required Documents</label>
                                {documentsToUpload.length === 0 && <p style={{fontSize: '12px', color: '#888'}}>No documents mapped for this permit type.</p>}
                                {documentsToUpload.filter(doc => {
                                    // Inspection Batch Certificate only becomes relevant once
                                    // Conditional/Monitoring is selected (or one is already attached)
                                    if ((doc.document_type || '').toUpperCase() === 'INSPECTION BATCH CERTIFICATE') {
                                        return !!releaseStatus || !!doc.existing_document;
                                    }
                                    return true;
                                }).map((doc) => {
                                    const isReplacing = replacingDocType === doc.document_type;
                                    const showFileInput = !doc.existing_document || isReplacing;

                                    // UI for a newly selected file
                                    if (doc.file_to_upload) {
                                        return (
                                            <div key={doc.document_type} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #eee' }}>
                                                <div style={{ flex: 1, fontWeight: 'bold', fontSize: '13px' }}>{doc.document_type}</div>
                                                <div style={{ flex: 2, display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <button 
                                                        type="button" 
                                                        className="btn-small btn-info-small" 
                                                        onClick={() => openFilePreview(doc.previewUrl, doc.file_to_upload.name)}
                                                    >
                                                        View
                                                    </button>
                                                    <button type="button" className="btn-small btn-danger-small" onClick={() => cancelFileSelection(doc.document_type)}>
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    }

                                    // Logic for existing docs or empty slots
                                    return (
                                        <div key={doc.document_type} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #eee' }}>
                                            <div style={{ flex: 1, fontWeight: 'bold', fontSize: '13px' }}>{doc.document_type}</div>
                                            <div style={{ flex: 2 }}>
                                                {showFileInput ? (
                                                    <input
                                                        type="file"
                                                        className="form-control"
                                                        onChange={(e) => handleFileChange(doc.document_type, e.target.files[0])}
                                                        disabled={saving || !hasPermission('PERMIT REPORTS', 'can_edit')}
                                                    />
                                                ) : (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                        <button 
                                                            type="button" 
                                                            className="btn-small btn-info-small" 
                                                            onClick={() => openFilePreview(doc.existing_document.document_file, doc.existing_document.document_file.split('/').pop())}
                                                        >
                                                            View
                                                        </button>
                                                        {/* 6. Conditionally render the Replace button */}
                                                        {hasPermission('PERMIT REPORTS', 'can_edit') && (
                                                            <button 
                                                                type="button" 
                                                                className="btn-small btn-warning-small" 
                                                                onClick={() => setReplacingDocType(doc.document_type)}
                                                            >
                                                                Replace
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <div>
                                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Application Number</label>
                                    <input
                                        type="text"
                                        value={authNumber}
                                        onChange={(e) => setAuthNumber(e.target.value)}
                                        className="form-control"
                                        style={{ width: '100%' }}
                                        disabled={saving}
                                        placeholder="Enter Application Number (Optional)"
                                    />
                                </div>
                                <div>
                                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 2 }}>Processing Status</label>
                                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                        {PROCESSING_STATUS_OPTIONS.map(option => (
                                            <label key={option.value} style={{ display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap', cursor: hasPermission('PERMIT REPORTS', 'can_edit') ? 'pointer' : 'default' }}>
                                                <input
                                                    type="radio"
                                                    name="processingStatus"
                                                    value={option.value}
                                                    checked={processingStatus === option.value}
                                                    onChange={(e) => setProcessingStatus(e.target.value)}
                                                    disabled={!hasPermission('PERMIT REPORTS', 'can_edit') || saving}
                                                />
                                                {option.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                {currentPermit && isConditionalPermitType(currentPermit.permitType) && (currentPermit.existingStatus === 'APPROVED' || isApproving) && (() => {
                                    const isPvocType = (currentPermit.permitType || '').toUpperCase().includes('PVOC');
                                    // Re-opening an already-approved permit shows the conditional
                                    // selections read-only — edits happen through status revert
                                    const conditionalReadOnly = currentPermit.existingStatus === 'APPROVED' && !isApproving;
                                    const conditionalEditable = hasPermission('PERMIT REPORTS', 'can_edit') && !conditionalReadOnly;
                                    return (
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, flexWrap: 'wrap' }}>
                                            <div>
                                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 2 }}>Sample Collected <span style={{ color: '#dc3545' }}>*</span></label>
                                                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                                    {[{ value: true, label: 'Yes' }, { value: false, label: 'No' }].map(option => (
                                                        <label key={String(option.value)} style={{ display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap', cursor: conditionalEditable ? 'pointer' : 'default' }}>
                                                            <input
                                                                type="radio"
                                                                name="sampleCollected"
                                                                checked={sampleCollected === option.value}
                                                                onChange={() => {
                                                                    setSampleCollected(option.value);
                                                                    if (!option.value) {
                                                                        setSampleCollectedDate('');
                                                                        setSampleLocation('');
                                                                        if (isPvocType) setReleaseStatus('');
                                                                    }
                                                                }}
                                                                disabled={!conditionalEditable || saving}
                                                            />
                                                            {option.label}
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                            {isPvocType && sampleCollected === true && (
                                                <div>
                                                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 2 }}>Release Status <span style={{ color: '#dc3545' }}>*</span></label>
                                                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                                        {getReleaseStatusOptions(currentPermit.permitType).map(option => (
                                                            <label key={option.value} style={{ display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap', cursor: conditionalEditable ? 'pointer' : 'default' }}>
                                                                <input
                                                                    type="radio"
                                                                    name="releaseStatus"
                                                                    value={option.value}
                                                                    checked={releaseStatus === option.value}
                                                                    onChange={(e) => setReleaseStatus(e.target.value)}
                                                                    disabled={!conditionalEditable || saving}
                                                                />
                                                                {option.label}
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        {sampleCollected === true && (!isPvocType || !!releaseStatus) && (
                                            <div style={{ marginTop: 10 }}>
                                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 2 }}>Collected At <span style={{ color: '#dc3545' }}>*</span></label>
                                                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                                    {[{ value: 'PORT', label: 'Port' }, { value: 'CLIENT_PREMISES', label: 'Client Premises' }].map(option => (
                                                        <label key={option.value} style={{ display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap', cursor: conditionalEditable ? 'pointer' : 'default' }}>
                                                            <input
                                                                type="radio"
                                                                name="sampleLocation"
                                                                value={option.value}
                                                                checked={sampleLocation === option.value}
                                                                onChange={(e) => setSampleLocation(e.target.value)}
                                                                disabled={!conditionalEditable || saving}
                                                            />
                                                            {option.label}
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {sampleCollected === true && (sampleLocation === 'PORT' || !!sampleCollectedDate) && (
                                            <div style={{ marginTop: 10, minWidth: 170, maxWidth: 220 }}>
                                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Date Collected <span style={{ color: '#dc3545' }}>*</span></label>
                                                <input
                                                    type="date"
                                                    value={sampleCollectedDate}
                                                    onChange={(e) => setSampleCollectedDate(e.target.value)}
                                                    max={new Date().toISOString().split('T')[0]}
                                                    className="form-control"
                                                    style={{ width: '100%' }}
                                                    disabled={!conditionalEditable || saving}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    );
                                })()}
                                <div>
                                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Remarks{currentPermit && isConditionalPermitType(currentPermit.permitType) && (currentPermit.existingStatus === 'APPROVED' || isApproving) ? ' *' : ''}</label>
                                    <textarea
                                        value={remarks}
                                        onChange={(e) => setRemarks(e.target.value)}
                                        className="form-control"
                                        rows="3"
                                        style={{ width: '100%' }}
                                        readOnly={!hasPermission('PERMIT REPORTS', 'can_edit')} // 8. Make textarea read-only
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="dialog-footer">
                            <button type="button" className="dialog-btn dialog-btn-secondary" onClick={handleCloseModal} disabled={saving}>Cancel</button>
                            {/* 9. Conditionally render the Save button */}
                            {hasPermission('PERMIT REPORTS', 'can_edit') && (
                                <button type="button" className="dialog-btn dialog-btn-primary" onClick={handleSavePermit} disabled={saving}>
                                    {saving ? 'Saving...' : 'Save'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Job Form Modal */}
            {jobFormModal.isOpen && (
                <div className="modal-overlay">
                    <div className="dialog-container" style={{
                        width: '95%',
                        maxWidth: '1400px',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <div className="dialog-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3>Job Details</h3>
                            <button className="icon-btn" onClick={closeJobFormModal} title="Close">
                                <i className="fa fa-times" />
                            </button>
                        </div>
                        <div className="dialog-body" style={{ padding: 0, overflowY: 'auto', flex: 1 }}>
                            {jobFormModal.job ? (
                                <JobForm
                                    mode="view"
                                    initialData={jobFormModal.job}
                                    onClose={closeJobFormModal}
                                />
                            ) : (
                                <div style={{ padding: 20 }}>Loading...</div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Add Manual Permit Modal */}
            {isAddPermitOpen && (
                 <div className="modal-overlay">
                    <div className="dialog-container" style={{ width: 400 }}>
                        <div className="dialog-header">
                            <h3>Add Required Permit</h3>
                        </div>
                        <div className="dialog-body">
                           <p>Add a permit requirement for Job #{jobToAddPermit?.id}.</p>
                           <label style={{ fontWeight: 'bold', display: 'block', marginTop: 10, marginBottom: 5 }}>Select Permit Type</label>
                           <Select2Dropdown
                                options={permitMappings.map(m => ({ id: m.permit_type, text: m.permit_type }))}
                                value={selectedPermitType}
                                onChange={setSelectedPermitType}
                                placeholder="Select permit..."
                                valueKey="id"
                                displayKey="text"
                           />
                        </div>
                        <div className="dialog-footer" style={{marginTop: 20}}>
                             <button type="button" className="dialog-btn dialog-btn-secondary" onClick={() => setIsAddPermitOpen(false)} disabled={saving}>Cancel</button>
                             <button type="button" className="dialog-btn dialog-btn-primary" onClick={handleSaveManualPermit} disabled={saving}>
                                 {saving ? 'Saving...' : 'Add Permit'}
                             </button>
                        </div>
                    </div>
                 </div>
            )}

        </div>
    );
};

export default PermitReport;